/**
 * Dev Tools Component
 *
 * Top-level, region-agnostic API testing page. The user picks a region
 * (drives `x-region` for every call) and optionally supplies a role ID to
 * prefill form fields from an organisation. Both selections are reflected
 * in the URL via query params (`?region=&roleId=`) so the page is
 * shareable and refresh-safe.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CheckCircleOutline as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Theme, ThemeProvider, createTheme } from "@mui/material/styles";
import { regionValuesArr } from "api";
import { useHistory, useLocation } from "react-router-dom";

import { Role } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { CallHistoryEntry } from "../../helpers/grpc-call-history";
import { isGrpcLoggingEnabled, setGrpcLogging } from "../../helpers/grpc-log";
import { clearSlateId, getSlateId, setSlateId } from "../../helpers/slate-id";
import { EnhancedResponseViewer } from "./components/enhanced-response-viewer";
import { DevToolsErrorBoundary } from "./components/error-boundary";
import { FormFieldRenderer } from "./components/form-field-renderer";
import { HistorySidePanel } from "./components/history-side-panel";
import { MethodSelector } from "./components/method-selector";
import { ServiceSelector } from "./components/service-selector";
import { TabPanel } from "./components/tab-panel";
import { AVAILABLE_SERVICES, MethodNames } from "./constants";
import { useCallHistory } from "./hooks/use-call-history";
import { MethodExecutionService, SchemaService } from "./services";
import { FormState, MethodDefinition, ServiceInfo } from "./types";
import {
  buildRequestObject,
  createEmptyFormState,
  prefillFormWithOrgData,
  updateFormField,
  validateField,
} from "./utils/form-utils";
import { logger } from "./utils/logger";
import { getOrgWithSubscription } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";

type LocationState = {
  orgName?: string;
};

const ALL_REGIONS = ["au", "us", "eu"] as const;
type Region = (typeof ALL_REGIONS)[number];
type RegionSelection = Region | "all";
const REGIONS = ALL_REGIONS.filter((r) =>
  regionValuesArr.includes(r),
) as unknown as typeof ALL_REGIONS;
const DEFAULT_REGION: RegionSelection = (REGIONS[0] ?? "au") as RegionSelection;

const isRegionSelection = (value: string | null): value is RegionSelection =>
  value !== null && ([...REGIONS, "all"] as string[]).includes(value);

/** Map a proto service typeName back to a dev-tools service id, if registered. */
const resolveServiceId = (serviceTypeName: string): string | null =>
  AVAILABLE_SERVICES.find((s) => s.service.typeName === serviceTypeName)?.id ??
  null;

// Shrinks the whole Dev Tools form's type scale to match the compact History
// panel, rather than one-off font-size overrides scattered across every field/
// label/button — nested ThemeProvider deep-merges with the app's outer theme
// (palette, etc. untouched).
const withCompactTypography = (outerTheme: Theme) =>
  createTheme(outerTheme, {
    typography: {
      body1: { fontSize: "0.9375rem" },
      body2: { fontSize: "0.875rem" },
      subtitle1: { fontSize: "0.9375rem" },
      subtitle2: { fontSize: "0.875rem" },
      caption: { fontSize: "0.8125rem" },
      overline: { fontSize: "0.75rem" },
      button: { fontSize: "0.875rem" },
    },
  });

interface SupportServiceFormProps {
  /**
   * True when rendered inside the global overlay (DevToolsOverlay) rather
   * than the standalone /dev-tools route. Suppresses reading/writing the URL
   * query string, since the overlay layers on top of whatever page the user
   * is currently on.
   */
  embedded?: boolean;
  /** Which top-level tab to land on. Defaults to "builder". */
  initialTopLevelTab?: "builder" | "settings";
  /**
   * Injectable page reload, matching DevToolsErrorBoundary. Defaults to
   * window.location.reload, which jsdom treats as unforgeable, so tests pass
   * a stub instead.
   */
  onReload?: () => void;
}

const SupportServiceForm: React.FC<SupportServiceFormProps> = ({
  embedded = false,
  initialTopLevelTab = "builder",
  onReload = () => window.location.reload(),
}) => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const orgName = embedded ? "" : (location.state?.orgName ?? "");

  // Read initial region + roleId from the URL query string. Both optional.
  const initialParams = useMemo(() => {
    if (embedded) {
      return { region: DEFAULT_REGION, roleId: "" };
    }
    const params = new URLSearchParams(location.search);
    const queryRegion = params.get("region");
    return {
      region: isRegionSelection(queryRegion) ? queryRegion : DEFAULT_REGION,
      roleId: params.get("roleId") ?? "",
    };
    // We deliberately only read the search string on first mount; afterwards
    // the form is the source of truth and we write back to the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [region, setRegion] = useState<RegionSelection>(initialParams.region);
  const [roleIdInput, setRoleIdInput] = useState<string>(initialParams.roleId);
  const [appliedRoleId, setAppliedRoleId] = useState<string>(
    initialParams.roleId,
  );
  const [prefillSource, setPrefillSource] = useState<Role | null>(null);
  const [isLoadingPrefill, setIsLoadingPrefill] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);

  const [availableServices, setAvailableServices] = useState<ServiceInfo[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>("support");
  const [methods, setMethods] = useState<MethodDefinition[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<MethodNames | "">("");
  const [formState, setFormState] = useState<FormState>({
    data: {},
    errors: {},
    touched: {},
    isValid: false,
    isSubmitting: false,
  });
  const [response, setResponse] = useState<unknown>(null);
  const [multiRegionResponses, setMultiRegionResponses] = useState<Record<
    Region,
    unknown
  > | null>(null);
  const [topLevelTab, setTopLevelTab] = useState<"builder" | "settings">(
    initialTopLevelTab,
  );
  const [activeTab, setActiveTab] = useState(0);
  const [multiRegionTab, setMultiRegionTab] = useState(0);
  const [resetSnackbar, setResetSnackbar] = useState(false);
  const [copied, setCopied] = useState(false);
  const [grpcLog, setGrpcLog] = useState<boolean>(isGrpcLoggingEnabled);
  const [slateIdInput, setSlateIdInput] = useState<string>(
    () => getSlateId() ?? "",
  );
  const [appliedSlateId, setAppliedSlateId] = useState<string>(
    () => getSlateId() ?? "",
  );
  // Live view of the call history (dev-tools + website), recorded by the
  // logging interceptor and kept in sync via the store subscription.
  const {
    history: callHistory,
    clear: clearCallHistory,
    togglePin: toggleCallHistoryPin,
  } = useCallHistory();
  // When a history entry is replayed we stash it here so the method-load and
  // prefill effects can restore the captured method/field values instead of
  // clearing the form or overwriting it with org-prefill defaults.
  const pendingReplayRef = useRef<CallHistoryEntry | null>(null);
  // Method whose form currently shows replay-restored values. While set, the
  // prefill effect leaves the form alone (even when a late-arriving prefill
  // source would otherwise clobber it). Cleared on any explicit user action
  // that should re-prefill (method change, apply/clear role id, service change).
  const replayOwnedMethodRef = useRef<string | null>(null);

  const handleGrpcLogToggle = (enabled: boolean) => {
    setGrpcLog(enabled);
    setGrpcLogging(enabled);
  };

  // The slate id is a cookie read by the gateway when it serves index.html, so
  // a reload is what actually swaps the build over to the selected slate.
  const commitSlateId = () => {
    const trimmed = slateIdInput.trim();
    if (trimmed) {
      setSlateId(trimmed);
    } else {
      clearSlateId();
    }
    setAppliedSlateId(trimmed);
    onReload();
  };

  const handleClearSlateId = () => {
    clearSlateId();
    setSlateIdInput("");
    setAppliedSlateId("");
    onReload();
  };

  const selectedMethodDef = useMemo(
    () => methods.find((m) => m.name === selectedMethod),
    [methods, selectedMethod],
  );

  const requestObject = useMemo(
    () => buildRequestObject(formState.data, selectedMethodDef?.inputSchema),
    [formState.data, selectedMethodDef],
  );

  // Sync region + applied role id back into the URL so refresh + copy-link both
  // work. history.replace (not push) so the back button doesn't fill with
  // intermediate states. Skip the very first render — the URL already matches
  // by construction.
  const isFirstSync = useRef(true);
  useEffect(() => {
    if (embedded) return;
    if (isFirstSync.current) {
      isFirstSync.current = false;
      return;
    }
    const params = new URLSearchParams();
    if (region !== DEFAULT_REGION) params.set("region", region);
    if (appliedRoleId) params.set("roleId", appliedRoleId);
    const search = params.toString();
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : "",
      state: location.state,
    });
    // location.pathname/search/state are read intentionally without depending
    // on `location` itself, which would cause an infinite loop after replace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, appliedRoleId, history]);

  // Load available services
  useEffect(() => {
    try {
      const services = SchemaService.getAvailableServices();
      setAvailableServices(services);
    } catch (error) {
      logger.error("Failed to load available services", { error });
    }
  }, []);

  // Load methods when service changes
  useEffect(() => {
    const loadMethods = async () => {
      try {
        const methodDefinitions =
          await SchemaService.getMethodDefinitionsForService(selectedServiceId);
        setMethods(methodDefinitions);

        // A replay may have switched the service; restore its method (and keep
        // the already-set response) rather than resetting the form.
        const pending = pendingReplayRef.current;
        if (
          pending &&
          resolveServiceId(pending.serviceTypeName) === selectedServiceId
        ) {
          setSelectedMethod(pending.methodName as MethodNames);
          return;
        }

        replayOwnedMethodRef.current = null;
        if (selectedMethod) setResetSnackbar(true);
        setSelectedMethod("");
        // Clearing the method drops the "Request Preview" tab, shifting the
        // remaining tab indices down; reset so activeTab can't point past the
        // new tab set and leave the panel blank.
        setActiveTab(0);
        setResponse(null);
        setMultiRegionResponses(null);
      } catch (error) {
        logger.error(
          `Failed to load methods for service ${selectedServiceId}`,
          { error },
        );
        setMethods([]);
      }
    };

    if (selectedServiceId) {
      loadMethods();
    }
  }, [selectedServiceId]);

  // Fetch the prefill source whenever an applied role id changes (initial
  // mount with query param, or explicit Apply click). Empty roleId clears.
  useEffect(() => {
    if (!appliedRoleId) {
      setPrefillSource(null);
      return;
    }
    let cancelled = false;
    setIsLoadingPrefill(true);
    getOrgWithSubscription(
      { roleId: appliedRoleId },
      callOptionsHeaders(region === "all" ? REGIONS[0] : region, appliedRoleId),
    )
      .then((response) => {
        if (cancelled) return;
        setPrefillSource(response.role ?? null);
      })
      .catch((error) => {
        if (cancelled) return;
        logger.error("Failed to load prefill source", { error });
        setPrefillSource(null);
        setPrefillError(
          "Could not find org — check the role ID and try again.",
        );
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingPrefill(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appliedRoleId, region]);

  // Prefill form when method or prefill source changes
  useEffect(() => {
    if (!selectedMethod || methods.length === 0) return;
    const method = methods.find((m) => m.name === selectedMethod);
    if (!method) return;

    // Replay: restore the captured field values verbatim, bypassing org
    // prefill. Mark this method as replay-owned so a later prefill-source
    // resolution (which re-runs this effect) doesn't clobber the restored form.
    const pending = pendingReplayRef.current;
    if (pending && pending.methodName === selectedMethod) {
      pendingReplayRef.current = null;
      replayOwnedMethodRef.current = selectedMethod;
      setFormState({
        ...createEmptyFormState(),
        data: (pending.request as Record<string, unknown>) ?? {},
      });
      return;
    }
    if (replayOwnedMethodRef.current === selectedMethod) return;

    let initialState = createEmptyFormState();

    const effectiveOrgData = prefillSource?.id
      ? prefillSource
      : appliedRoleId
        ? ({ id: appliedRoleId } as Role)
        : null;

    if (effectiveOrgData?.id && method.inputSchema) {
      try {
        initialState = prefillFormWithOrgData(
          initialState,
          effectiveOrgData,
          method.inputSchema,
        );
      } catch (error) {
        logger.error("Error during prefill", { error });
      }
    }

    setFormState(initialState);
  }, [selectedMethod, methods, prefillSource, appliedRoleId]);

  const handleApplyRoleId = () => {
    replayOwnedMethodRef.current = null;
    setPrefillError(null);
    setAppliedRoleId(roleIdInput.trim());
  };

  const handleClearRoleId = () => {
    replayOwnedMethodRef.current = null;
    setRoleIdInput("");
    setAppliedRoleId("");
    setPrefillSource(null);
    setPrefillError(null);
  };

  // Event handlers with enhanced error handling
  const handleInputChange = useCallback(
    (fieldName: string, value: unknown) => {
      setFormState((prevState) => {
        try {
          const updatedState = updateFormField(prevState, fieldName, value);

          return updatedState;
        } catch (error) {
          logger.error("Error in handleInputChange", { error });
          return prevState;
        }
      });
    },
    [methods, selectedMethod],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMethod) return;

    setFormState((prev) => ({ ...prev, isSubmitting: true }));
    setMultiRegionTab(0);

    try {
      if (!selectedMethodDef) {
        throw new Error(
          `Method ${selectedMethod} not found or improperly configured`,
        );
      }

      if (region === "all") {
        const results = await Promise.all(
          REGIONS.map((r) =>
            MethodExecutionService.executeMethodForService(
              selectedServiceId,
              selectedMethod,
              requestObject,
              { region: r, referenceId: appliedRoleId || undefined },
            ),
          ),
        );
        const byRegion = Object.fromEntries(
          REGIONS.map((r, i) => [r, results[i]]),
        ) as Record<Region, unknown>;
        setMultiRegionResponses(byRegion);
        setResponse(null);
      } else {
        const result = await MethodExecutionService.executeMethodForService(
          selectedServiceId,
          selectedMethod,
          requestObject,
          { region, referenceId: appliedRoleId || undefined },
        );
        setResponse(result);
        setMultiRegionResponses(null);
      }
      setActiveTab(1);
    } catch (error) {
      logger.error("Method execution error", { error });
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setResponse({
        error: errorMessage,
        details: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
      });
      // Switch to Response tab even on error to show the error
      setActiveTab(1);
    } finally {
      setFormState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  // Whether a history entry can be replayed into the form (only calls to a
  // dev-tools-registered service — currently SupportService — are replayable).
  const canReplay = (entry: CallHistoryEntry): boolean =>
    resolveServiceId(entry.serviceTypeName) !== null;

  // Populate-only replay: restore the form (service/method/region/role id +
  // field values) and show the stored response. The user re-runs via Execute.
  const handleReplay = (entry: CallHistoryEntry) => {
    const serviceId = resolveServiceId(entry.serviceTypeName);
    if (!serviceId) return; // Non-dev-tools service — log-only, not replayable.

    pendingReplayRef.current = entry;

    const replayRegion = entry.region ?? null;
    setRegion(isRegionSelection(replayRegion) ? replayRegion : DEFAULT_REGION);
    setRoleIdInput(entry.referenceId ?? "");
    setAppliedRoleId(entry.referenceId ?? "");
    setResponse(entry.result ?? null);
    setMultiRegionResponses(null);

    if (serviceId !== selectedServiceId) {
      // Service switch: the methods-load effect applies the pending method and
      // the prefill effect then restores the fields from pendingReplayRef.
      setSelectedServiceId(serviceId);
    } else {
      // Same service: restore the fields directly — the prefill effect won't
      // re-run when the method (and role id) are unchanged. Mark the method as
      // replay-owned so a later prefill pass doesn't clobber the restored form.
      pendingReplayRef.current = null;
      replayOwnedMethodRef.current = entry.methodName;
      setSelectedMethod(entry.methodName as MethodNames);
      setFormState({
        ...createEmptyFormState(),
        data: (entry.request as Record<string, unknown>) ?? {},
      });
    }

    // Show the form / request preview so the restored fields are visible.
    setActiveTab(0);
  };

  const handleClearHistory = () => {
    clearCallHistory();
  };

  const handleCopyRequest = async () => {
    try {
      const requestJson = JSON.stringify(requestObject, null, 2);
      await navigator.clipboard.writeText(requestJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error("Failed to copy to clipboard", { error });
    }
  };

  const renderMethodInputs = () => {
    const selectedMethodDef = methods.find(
      (method) => method.name === selectedMethod,
    );
    if (!selectedMethodDef?.inputSchema) return null;

    return (
      <FormFieldRenderer
        fields={selectedMethodDef.inputSchema}
        formState={formState}
        onFieldChange={handleInputChange}
      />
    );
  };

  return (
    <ThemeProvider theme={withCompactTypography}>
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ paddingBottom: "16px" }}>
          <Tabs
            value={topLevelTab}
            onChange={(_, value) => setTopLevelTab(value)}
            aria-label="Dev Tools sections"
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab
              label="Request Builder"
              value="builder"
              data-testid="dev-tools-top-tab-builder"
            />
            <Tab
              label="Settings"
              value="settings"
              data-testid="dev-tools-top-tab-settings"
            />
          </Tabs>

          {topLevelTab === "settings" ? (
            <Paper variant="outlined" sx={{ p: 2, maxWidth: 480 }}>
              <Tooltip title="Log every gRPC request/response and its duration to the browser console">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={grpcLog}
                      onChange={(e) => handleGrpcLogToggle(e.target.checked)}
                      data-testid="dev-tools-grpc-log-toggle"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Log gRPC to console
                    </Typography>
                  }
                  sx={{ ml: 0, mr: 0, mb: 2, display: "flex" }}
                />
              </Tooltip>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="Sets the SC-SLATE-ID header on all API requests. Only active in dev environment.">
                  <TextField
                    size="small"
                    label="Slate ID (optional)"
                    value={slateIdInput}
                    onChange={(event) => setSlateIdInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        commitSlateId();
                      }
                    }}
                    fullWidth
                    inputProps={{ "data-testid": "dev-tools-slate-id-input" }}
                  />
                </Tooltip>
                <Button
                  variant="contained"
                  size="small"
                  onClick={commitSlateId}
                  disabled={
                    slateIdInput.trim() === appliedSlateId && !!appliedSlateId
                  }
                  color={
                    slateIdInput.trim() === appliedSlateId && !!appliedSlateId
                      ? "success"
                      : "primary"
                  }
                  startIcon={
                    slateIdInput.trim() === appliedSlateId &&
                    !!appliedSlateId ? (
                      <CheckCircleIcon />
                    ) : null
                  }
                  sx={{
                    px: 2,
                    minWidth: 100,
                    height: 40,
                    whiteSpace: "nowrap",
                  }}
                  data-testid="dev-tools-save-slate-id"
                >
                  {slateIdInput.trim() === appliedSlateId && !!appliedSlateId
                    ? "Saved"
                    : "Save"}
                </Button>
                {slateIdInput && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClearSlateId}
                    sx={{ height: 40 }}
                    data-testid="dev-tools-clear-slate-id"
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "stretch",
                    flexWrap: "wrap",
                  }}
                >
                  <Paper
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1,
                      py: 0,
                      borderRadius: 1.5,
                      backgroundColor: "action.hover",
                    }}
                    elevation={0}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Region
                    </Typography>
                    <ToggleButtonGroup
                      value={region}
                      exclusive
                      onChange={(_, value) => {
                        if (value !== null) setRegion(value);
                      }}
                      size="small"
                      aria-label="region"
                      data-testid="dev-tools-region-select"
                      sx={{
                        gap: 0.5,
                        "& .MuiToggleButtonGroup-grouped": {
                          border: "none !important",
                          borderRadius: "6px !important",
                          px: 1.5,
                          py: 0.25,
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          color: "text.secondary",
                          backgroundColor: "transparent",
                          "&.Mui-selected": {
                            backgroundColor: "background.paper",
                            color: "text.primary",
                            fontWeight: 600,
                            boxShadow: 1,
                            "&:hover": { backgroundColor: "background.paper" },
                          },
                        },
                      }}
                    >
                      {REGIONS.map((r) => (
                        <ToggleButton
                          key={r}
                          value={r}
                          aria-label={r}
                          disableRipple
                        >
                          {region === r && (
                            <Box
                              component="span"
                              sx={{
                                color: "primary.main !important",
                                mr: 0.5,
                                fontSize: "0.5rem",
                              }}
                            >
                              ●
                            </Box>
                          )}
                          {r.toUpperCase()}
                        </ToggleButton>
                      ))}
                      <ToggleButton value="all" aria-label="all" disableRipple>
                        {region === "all" && (
                          <Box
                            component="span"
                            sx={{
                              color: "primary.main !important",
                              mr: 0.5,
                              fontSize: "0.5rem",
                            }}
                          >
                            ●
                          </Box>
                        )}
                        All
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Paper>

                  <TextField
                    size="small"
                    label="Prefill from role ID (optional)"
                    value={roleIdInput}
                    onChange={(event) => setRoleIdInput(event.target.value)}
                    onBlur={() => {
                      if (roleIdInput.trim() !== appliedRoleId)
                        handleApplyRoleId();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleApplyRoleId();
                      }
                    }}
                    sx={{ minWidth: 320, flexGrow: 1 }}
                    inputProps={{ "data-testid": "dev-tools-role-id-input" }}
                  />

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleApplyRoleId}
                    disabled={
                      isLoadingPrefill ||
                      roleIdInput.trim() === "" ||
                      (roleIdInput.trim() === appliedRoleId && !!prefillSource)
                    }
                    color={
                      roleIdInput.trim() === appliedRoleId && !!prefillSource
                        ? "success"
                        : "primary"
                    }
                    startIcon={
                      roleIdInput.trim() === appliedRoleId &&
                      !!prefillSource ? (
                        <CheckCircleIcon />
                      ) : null
                    }
                    data-testid="dev-tools-apply-role-id"
                  >
                    {isLoadingPrefill ? (
                      <CircularProgress size={16} />
                    ) : roleIdInput.trim() === appliedRoleId &&
                      !!prefillSource ? (
                      "Applied"
                    ) : (
                      "Apply"
                    )}
                  </Button>

                  {(appliedRoleId || prefillSource) && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleClearRoleId}
                      data-testid="dev-tools-clear-role-id"
                    >
                      Clear
                    </Button>
                  )}

                  {prefillSource?.name && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexBasis: "100%",
                      }}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Prefilling from ${prefillSource.name}`}
                        data-testid="dev-tools-prefill-source-chip"
                      />
                    </Box>
                  )}
                  {prefillError && (
                    <Alert
                      severity="error"
                      sx={{ py: 0, px: 1 }}
                      onClose={() => setPrefillError(null)}
                    >
                      {prefillError}
                    </Alert>
                  )}
                </Box>
              </Paper>

              <form onSubmit={handleSubmit}>
                {/* Service and Method Selection */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <ServiceSelector
                    selectedServiceId={selectedServiceId}
                    onServiceChange={setSelectedServiceId}
                    services={availableServices}
                  />

                  <MethodSelector
                    methods={methods}
                    selectedMethod={selectedMethod}
                    onMethodChange={(methodName) => {
                      replayOwnedMethodRef.current = null;
                      setSelectedMethod(methodName as MethodNames);
                    }}
                  />
                </Box>

                {/* Side-by-side layout for Form and Results */}
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  {/* Left side - Form Input */}
                  <Grid item xs={12} md={4}>
                    <Paper
                      variant="outlined"
                      sx={{
                        padding: 2,
                        minHeight: selectedMethod ? 160 : "auto",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {selectedMethod && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1.5,
                          }}
                        >
                          <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{ lineHeight: 1 }}
                          >
                            Request
                          </Typography>
                          {selectedMethodDef &&
                            selectedMethodDef.inputSchema.length > 0 && (
                              <Chip
                                label={`${selectedMethodDef.inputSchema.length} fields`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                        </Box>
                      )}
                      <Box sx={{ mb: 1 }}>
                        {isLoadingPrefill ? (
                          <Box sx={{ py: 2, textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary">
                              Loading prefill source…
                            </Typography>
                          </Box>
                        ) : (
                          renderMethodInputs()
                        )}
                      </Box>

                      <Tooltip
                        title={!selectedMethod ? "Select a method first" : ""}
                      >
                        <span>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={formState.isSubmitting || !selectedMethod}
                            startIcon={
                              formState.isSubmitting ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : null
                            }
                            sx={{ mt: 1 }}
                          >
                            {formState.isSubmitting
                              ? "Submitting..."
                              : "Execute Method"}
                          </Button>
                        </span>
                      </Tooltip>
                    </Paper>
                  </Grid>

                  {/* Right side - Results */}
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: "flex", gap: 2, minWidth: 0 }}>
                      <Paper
                        sx={{
                          padding: 2,
                          minHeight: 400,
                          display: "flex",
                          flexDirection: "column",
                          flexGrow: 1,
                          minWidth: 0,
                        }}
                      >
                        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                          <Tabs
                            value={activeTab}
                            onChange={(event, newValue) =>
                              setActiveTab(newValue)
                            }
                            aria-label="Result tabs"
                          >
                            {selectedMethod && <Tab label="Request Preview" />}
                            <Tab label="Response" />
                          </Tabs>
                        </Box>

                        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                          {/* Request Preview Tab - Shows current form state */}
                          {selectedMethod && (
                            <TabPanel value={activeTab} index={0}>
                              <Box sx={{ textAlign: "left" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    mb: 1.5,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{ mb: 0.5, fontWeight: 600 }}
                                    >
                                      Request Preview
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      JSON request that will be sent to the API
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 0.5,
                                      alignItems: "center",
                                    }}
                                  >
                                    {(() => {
                                      const fieldCount =
                                        Object.keys(requestObject).length;
                                      const requestSize =
                                        fieldCount > 0
                                          ? new Blob([
                                              JSON.stringify(requestObject),
                                            ]).size
                                          : 0;
                                      const sizeLabel =
                                        requestSize < 1024
                                          ? `${requestSize} B`
                                          : `${(requestSize / 1024).toFixed(
                                              1,
                                            )} KB`;

                                      return fieldCount > 0 ? (
                                        <>
                                          <Chip
                                            label={`${fieldCount} field${
                                              fieldCount !== 1 ? "s" : ""
                                            }`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                          />
                                          <Chip
                                            label={sizeLabel}
                                            size="small"
                                            color="default"
                                            variant="outlined"
                                          />
                                          <Tooltip
                                            title={
                                              copied
                                                ? "Copied!"
                                                : "Copy to clipboard"
                                            }
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={handleCopyRequest}
                                              sx={{ ml: 0.5 }}
                                            >
                                              <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      ) : null;
                                    })()}
                                  </Box>
                                </Box>
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 2,
                                    bgcolor: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "grey.900"
                                        : "grey.50",
                                    border: (theme) =>
                                      `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <pre
                                    style={{
                                      margin: 0,
                                      fontFamily: "monospace",
                                      fontSize: "0.875rem",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",

                                      overflow: "auto",
                                      color: "inherit",
                                    }}
                                  >
                                    {(() => {
                                      const hasData =
                                        Object.keys(requestObject).length > 0;

                                      return hasData
                                        ? JSON.stringify(requestObject, null, 2)
                                        : "// No data entered yet.\n// Fill out the form fields to see the request preview.";
                                    })()}
                                  </pre>
                                </Paper>
                              </Box>
                            </TabPanel>
                          )}

                          {/* Response Tab */}
                          <TabPanel
                            value={activeTab}
                            index={selectedMethod ? 1 : 0}
                          >
                            {formState.isSubmitting ? (
                              <Box sx={{ textAlign: "center", py: 4 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Executing method...
                                </Typography>
                              </Box>
                            ) : multiRegionResponses !== null ? (
                              <Box>
                                <Tabs
                                  value={multiRegionTab}
                                  onChange={(_, v) => setMultiRegionTab(v)}
                                  sx={{
                                    mb: 2,
                                    minHeight: 36,
                                    "& .MuiTab-root": {
                                      minHeight: 36,
                                      py: 0.5,
                                    },
                                  }}
                                >
                                  {REGIONS.map((r) => (
                                    <Tab key={r} label={r.toUpperCase()} />
                                  ))}
                                </Tabs>
                                {REGIONS.map((r, i) => (
                                  <Box key={r} hidden={multiRegionTab !== i}>
                                    <EnhancedResponseViewer
                                      response={multiRegionResponses[r]}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            ) : response !== null ? (
                              <EnhancedResponseViewer response={response} />
                            ) : (
                              <Box sx={{ textAlign: "center", py: 4 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Execute a method to see the response here
                                </Typography>
                              </Box>
                            )}
                          </TabPanel>
                        </Box>
                      </Paper>
                      <HistorySidePanel
                        entries={callHistory}
                        onReplay={handleReplay}
                        onClear={handleClearHistory}
                        onTogglePin={toggleCallHistoryPin}
                        canReplay={canReplay}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </>
          )}
        </Grid>
      </Grid>
      <Snackbar
        open={resetSnackbar}
        autoHideDuration={3000}
        onClose={() => setResetSnackbar(false)}
        message="Form reset — service changed"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </ThemeProvider>
  );
};

/**
 * Main Dev Tools Component with Error Boundary
 *
 * Wraps the core functionality in an error boundary for production safety
 */
const DevToolsWithErrorBoundary: React.FC<SupportServiceFormProps> = ({
  embedded,
  initialTopLevelTab,
  onReload,
}) => {
  return (
    <DevToolsErrorBoundary>
      <SupportServiceForm
        embedded={embedded}
        initialTopLevelTab={initialTopLevelTab}
        onReload={onReload}
      />
    </DevToolsErrorBoundary>
  );
};

export default DevToolsWithErrorBoundary;
